import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148, 163, 184, ${p.opacity})`;
        ctx.fill();
      });
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(148, 163, 184, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0F172A]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/bg-tech-dark.svg"
          alt="hero background"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#0F172A]/80 to-[#1E293B]" />
      </div>

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-20">
        <div className="max-w-3xl">
          {/* Tag */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 mb-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F97316]" />
            <span className="text-xs text-white/60 tracking-wide" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
              AI 时代的软件生产方式重构
            </span>
          </div>

          {/* Main Title */}
          <h1
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-8"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
          >
            软件开发正在经历
            <br />
            <span className="text-white/80">一次根本性的</span>
            <br />
            <span className="text-[#2563EB]">变革</span>
          </h1>

          {/* Sub Title */}
          <p
            className="text-xl md:text-2xl text-white/50 font-medium mb-8"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
          >
            不是工具升级，而是生产方式重构。
          </p>

          {/* Body */}
          <p
            className="text-base md:text-lg text-white/40 leading-relaxed mb-12 max-w-xl"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
          >
            AI 正在压缩软件交付成本，让过去做不了的事情，现在值得重新做。
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link
              to="/#premise"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0F172A] text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-white/90 transition-all duration-200"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              开始了解
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <a
              href="#contact-section"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/60 hover:bg-white/5 transition-all duration-200"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约交流
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>SCROLL</span>
          <i className="ri-arrow-down-line text-white/30 text-sm" />
        </div>
      </div>
    </section>
  );
}
